// Plain stand-in for MUI-Dialog as ClientVideoFeedback used it (non-fullscreen, maxWidth="lg"):
// portal to <body>, dimmed backdrop, centred paper with MUI's Zoom-in (100ms delay), Escape and
// backdrop click close, body scroll locked the way MUI's Modal does it (overflow hidden + scrollbar
// compensation) plus Lenis paused through the same useSmoothScrollLock hook.
import {memo, useEffect, useRef, useState} from 'react';
import type {ReactNode} from 'react';
import {createPortal} from 'react-dom';
import useSmoothScrollLock from '@/hooks/useSmoothScrollLock';
import styles from './VideoDialog.module.scss';

// MUI's leavingScreen duration (195ms) + the Zoom's 100ms transition delay.
const EXIT_MS = 295;

interface VideoDialogProps {
    open: boolean;
    onClose: () => void;
    children: ReactNode;
}

const useBodyScrollLock = (locked: boolean) => {
    useEffect(() => {
        if (!locked) return undefined;
        const {body, documentElement} = document;
        const prevOverflow = body.style.overflow;
        const prevPaddingRight = body.style.paddingRight;
        const scrollbar = globalThis.innerWidth - documentElement.clientWidth;
        if (scrollbar > 0) {
            const current = Number.parseFloat(globalThis.getComputedStyle(body).paddingRight) || 0;
            body.style.paddingRight = `${current + scrollbar}px`;
        }
        body.style.overflow = 'hidden';
        return () => {
            body.style.overflow = prevOverflow;
            body.style.paddingRight = prevPaddingRight;
        };
    }, [locked]);
};

const VideoDialog = ({open, onClose, children}: VideoDialogProps) => {
    const [mounted, setMounted] = useState(open);
    const paperRef = useRef<HTMLDivElement>(null);
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    useSmoothScrollLock(open);
    useBodyScrollLock(mounted);

    // Stay mounted through the exit animation, like MUI's transition does.
    useEffect(() => {
        if (open) {
            setMounted(true);
            return undefined;
        }
        const id = globalThis.setTimeout(() => setMounted(false), EXIT_MS);
        return () => globalThis.clearTimeout(id);
    }, [open]);

    // Escape closes; focus moves into the dialog and back to the trigger afterwards.
    useEffect(() => {
        if (!open) return undefined;
        const previous = document.activeElement as HTMLElement | null;
        paperRef.current?.focus();
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onCloseRef.current();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('keydown', onKeyDown);
            previous?.focus?.();
        };
    }, [open]);

    if (!mounted || typeof document === 'undefined') return null;

    return createPortal(
        <div className={[styles.root, !open && styles.closing].filter(Boolean).join(' ')} role="presentation">
            <div className={styles.backdrop} aria-hidden="true" />
            <div
                className={styles.container}
                role="presentation"
                onClick={(event) => {
                    if (event.target === event.currentTarget) onClose();
                }}
            >
                <div ref={paperRef} className={styles.paper} role="dialog" aria-modal="true" tabIndex={-1}>
                    {children}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default memo(VideoDialog);
